import { useEffect, useRef, useState } from 'react';

/**
 * useAutoSaveToCloud — debounced PUT of the current doc bytes back to cloud
 * whenever the user has unsaved changes and the doc is cloud-sourced.
 *
 * Intentionally simple: we lean on the Viewer's `usePrecomputedSave` for the
 * byte generation (it's already debouncing computes at 500 ms), then add our
 * own longer idle window (default 2 s) before actually firing the PUT. This
 * keeps network traffic proportional to "user paused typing", not to every
 * keystroke.
 *
 * Disables itself when:
 *   - cloudState !== 'available'
 *   - docSource !== 'cloud'
 *   - !hasUnsavedChanges
 *   - already firing a save (in-flight ref)
 *
 * Error handling: `onSave` is expected to set its own sync status (synced /
 * saving / error) and surface errors via the SourceChip. This hook only
 * triggers the call; it doesn't reason about transport failures.
 *
 * @param {Object} params
 * @param {boolean} params.enabled - master kill switch (docSource === 'cloud' && cloudState === 'available')
 * @param {boolean} params.hasUnsavedChanges - from the Viewer's dirty tracking
 * @param {Function} params.onSave - async () => void. Expected: handleSaveToCloud({silent: true})
 * @param {number} [params.idleMs=2000] - debounce window (ms)
 */
export default function useAutoSaveToCloud({
  enabled,
  hasUnsavedChanges,
  onSave,
  revision,
  idleMs = 2000,
}) {
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const revisionRef = useRef(revision);
  revisionRef.current = revision;
  const mounted = useRef(true);
  const [completed, setCompleted] = useState(0);

  useEffect(() => {
    // Cancel any pending timer whenever inputs change.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!enabled || !hasUnsavedChanges) return undefined;

    timerRef.current = setTimeout(async () => {
      timerRef.current = null;
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      const savingRevision = revisionRef.current;
      try {
        await onSaveRef.current?.();
      } catch {
        // Swallow — the save handler has already flagged the SourceChip to
        // 'error' state. Future user edits will re-trigger this timer, which
        // gives us automatic retry-on-next-change semantics for free.
      } finally {
        inFlightRef.current = false;
        // Edits made while a save was uploading still need their own save.
        if (mounted.current && revisionRef.current !== savingRevision) setCompleted((value) => value + 1);
      }
    }, idleMs);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [enabled, hasUnsavedChanges, revision, completed, idleMs]);

  // Final cleanup on unmount — if there's a pending save timer, discard it.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);
}
